import React, { useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, Link, useParams, useNavigate } from "react-router-dom";
import { api, API, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Calendar, Users, Ticket, QrCode, CheckSquare, BarChart3, Plus, Trash2,
  Copy, ExternalLink, Upload, ArrowLeft, Camera, ClipboardCheck, Clock,
  ShieldCheck, Search, X, ChevronRight, FileSpreadsheet, FileText, Mail, TrendingUp, RefreshCw,
} from "lucide-react";
import { DashboardShell, StatCard, SectionTitle, Btn, Input, Modal, EmptyState } from "@/components/DashUI";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

/* ============================================================
   Root routes — mounted from OrgAdmin
   ============================================================ */
export function VisitorManagement({ nav }) {
  return (
    <Routes>
      <Route index element={<EventsListVM nav={nav} />} />
      <Route path=":eventId" element={<EventDetailVM nav={nav} />} />
      <Route path=":eventId/attendees" element={<EventDetailVM nav={nav} tab="attendees" />} />
      <Route path=":eventId/tickets" element={<EventDetailVM nav={nav} tab="tickets" />} />
      <Route path=":eventId/approvals" element={<EventDetailVM nav={nav} tab="approvals" />} />
      <Route path=":eventId/checkin" element={<EventDetailVM nav={nav} tab="checkin" />} />
      <Route path=":eventId/analytics" element={<EventDetailVM nav={nav} tab="analytics" />} />
      <Route path=":eventId/settings" element={<EventDetailVM nav={nav} tab="settings" />} />
    </Routes>
  );
}

/* ============================================================
   Events list
   ============================================================ */
function EventsListVM({ nav }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(defaultEventForm());
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/vm/events");
      setRows(data.events || []);
    } catch { toast.error("Failed to load events"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function create(e) {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Event name is required"); return; }
    if (form.ticket_start && form.ticket_end && Number(form.ticket_end) < Number(form.ticket_start)) {
      toast.error("Ticket end must be greater than or equal to ticket start"); return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        capacity: Number(form.capacity) || 0,
        ticket_start: Number(form.ticket_start) || 0,
        ticket_end: Number(form.ticket_end) || 0,
      };
      const { data } = await api.post("/vm/events", body);
      setModal(false);
      setForm(defaultEventForm());
      toast.success("Event created");
      navigate(`/dashboard/visitor-management/${data.event.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  }

  return (
    <DashboardShell title="Visitor Management" subtitle="Visitor Management" nav={nav} testId="vm-events">
      <SectionTitle
        title="Events"
        action={<Btn onClick={() => setModal(true)} testId="vm-new-event"><Plus size={14} /> New Event</Btn>}
      />

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Calendar size={18} />}
          title="No events yet"
          body="Create an event to generate tickets, invite attendees and start check-ins."
          action={<Btn onClick={() => setModal(true)} testId="vm-empty-create"><Plus size={14} /> New Event</Btn>}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((e) => <EventCard key={e.id} ev={e} />)}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title="Create event">
        <form onSubmit={create} className="space-y-3" data-testid="vm-event-form">
          <Input label="Event name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testId="vm-input-name" />
          <Input label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} rows={3} testId="vm-input-desc" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Date" type="date" value={form.event_date} onChange={(v) => setForm({ ...form, event_date: v })} testId="vm-input-date" />
            <Input label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testId="vm-input-location" />
            <Input label="Start time" type="time" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testId="vm-input-start" />
            <Input label="End time" type="time" value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} testId="vm-input-end" />
          </div>
          <Input label="Capacity (0 = unlimited)" type="number" value={form.capacity} onChange={(v) => setForm({ ...form, capacity: v })} testId="vm-input-capacity" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Ticket sequence — start" type="number" value={form.ticket_start} onChange={(v) => setForm({ ...form, ticket_start: v })} placeholder="1000" testId="vm-input-ticket-start" />
            <Input label="Ticket sequence — end" type="number" value={form.ticket_end} onChange={(v) => setForm({ ...form, ticket_end: v })} placeholder="2000" testId="vm-input-ticket-end" />
          </div>
          <div className="text-xs text-gray-500 -mt-1">Tickets and unique QR codes are generated automatically.</div>

          <div className="pt-3 flex justify-end gap-2">
            <Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn type="submit" disabled={saving} testId="vm-event-save">{saving ? "Creating…" : "Create event"}</Btn>
          </div>
        </form>
      </Modal>
    </DashboardShell>
  );
}

function defaultEventForm() {
  return {
    name: "", description: "", event_date: "", start_time: "", end_time: "",
    location: "", capacity: "", ticket_start: "", ticket_end: "",
  };
}

function EventCard({ ev }) {
  const m = ev.metrics || {};
  return (
    <Link
      to={`/dashboard/visitor-management/${ev.id}`}
      className="block border border-gray-200 hover:border-gray-300 rounded-xl p-5 transition-colors group"
      data-testid={`vm-event-card-${ev.id}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">
            {ev.event_date || "No date"}
          </div>
          <div className="font-display text-lg font-semibold tracking-tight text-gray-900 mt-1">{ev.name}</div>
          <div className="text-xs text-gray-500 mt-1">{ev.location || "—"}</div>
        </div>
        <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-700 shrink-0 mt-1" />
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <MiniStat label="Tickets" value={m.total_tickets ?? 0} />
        <MiniStat label="Registered" value={(m.registered_tickets || 0) + (m.checked_in || 0)} />
        <MiniStat label="Checked in" value={m.checked_in ?? 0} />
      </div>
    </Link>
  );
}
function MiniStat({ label, value }) {
  return (
    <div className="border border-gray-100 rounded-md py-2">
      <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">{label}</div>
      <div className="font-display text-lg font-semibold text-gray-900">{value}</div>
    </div>
  );
}

/* ============================================================
   Event detail with tabs
   ============================================================ */
function EventDetailVM({ nav, tab: initialTab }) {
  const { eventId } = useParams();
  const [ctx, setCtx] = useState(null);
  const [tab, setTab] = useState(initialTab || "overview");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/vm/events/${eventId}`);
      setCtx(data);
    } catch { toast.error("Failed to load event"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [eventId]);

  if (loading || !ctx) {
    return <DashboardShell title="Visitor Management" subtitle="Loading…" nav={nav}><div className="text-sm text-gray-500">Loading…</div></DashboardShell>;
  }

  const ev = ctx.event;
  const m = ctx.metrics;

  return (
    <DashboardShell title="Visitor Management" subtitle={ev.name} nav={nav} testId="vm-event-detail">
      <Link to="/dashboard/visitor-management" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 mb-4" data-testid="vm-back">
        <ArrowLeft size={14} /> All events
      </Link>

      <div className="border-b border-gray-200 mb-6 flex flex-wrap gap-1">
        {[
          { id: "overview",  label: "Overview",   Icon: BarChart3 },
          { id: "attendees", label: "Attendees",  Icon: Users },
          { id: "tickets",   label: "Tickets",    Icon: Ticket },
          { id: "approvals", label: `Approvals${m.pending_registrations ? ` · ${m.pending_registrations}` : ""}`, Icon: ClipboardCheck },
          { id: "checkin",   label: "Check-in",   Icon: CheckSquare },
          { id: "analytics", label: "Analytics",  Icon: TrendingUp },
          { id: "settings",  label: "Settings",   Icon: QrCode },
        ].map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-2 text-sm px-3 py-2.5 border-b-2 -mb-px transition-colors ${
              tab === id ? "border-gray-900 text-gray-900 font-medium" : "border-transparent text-gray-500 hover:text-gray-900"
            }`}
            data-testid={`vm-tab-${id}`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab ev={ev} m={m} reload={load} />}
      {tab === "attendees" && <AttendeesTab ev={ev} reload={load} />}
      {tab === "tickets" && <TicketsTab ev={ev} reload={load} />}
      {tab === "approvals" && <ApprovalsTab ev={ev} reload={load} />}
      {tab === "checkin" && <CheckinTab ev={ev} reload={load} />}
      {tab === "analytics" && <AnalyticsTab ev={ev} />}
      {tab === "settings" && <SettingsTab ev={ev} reload={load} />}
    </DashboardShell>
  );
}

/* ============================================================
   Overview tab — dashboard metrics + quick actions
   ============================================================ */
function OverviewTab({ ev, m, reload }) {
  const publicRegUrl = `${window.location.origin}/vm/register/${ev.slug}`;

  function download(kind) {
    const url = `${API}/vm/events/${ev.id}/report.${kind}`;
    const token = localStorage.getItem("expoflo_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${ev.slug}-report.${kind}`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      })
      .catch(() => toast.error(`Could not download ${kind.toUpperCase()}`));
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Capacity" value={m.capacity || "—"} testId="vm-stat-capacity" />
        <StatCard label="Total tickets" value={m.total_tickets} testId="vm-stat-tickets" />
        <StatCard label="Available" value={m.available_tickets} testId="vm-stat-available" />
        <StatCard label="Registered" value={m.approved_registrations} testId="vm-stat-registered" />
        <StatCard label="Pending" value={m.pending_registrations} testId="vm-stat-pending" />
        <StatCard label="Checked in" value={m.checked_in} testId="vm-stat-checkedin" />
        <StatCard label="No-shows" value={m.no_shows} testId="vm-stat-noshows" />
        <StatCard label="Check-in rate" value={`${m.check_in_rate}%`} testId="vm-stat-rate" />
      </div>

      <div className="border border-gray-200 rounded-xl p-5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">Public registration link</div>
        <div className="mt-2 flex items-center gap-2">
          <input readOnly value={publicRegUrl} className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50" data-testid="vm-public-reg-url" />
          <Btn variant="secondary" onClick={() => { navigator.clipboard.writeText(publicRegUrl); toast.success("Link copied"); }} testId="vm-copy-public"><Copy size={14} /> Copy</Btn>
          <a href={publicRegUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-gray-700 border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50" data-testid="vm-open-public"><ExternalLink size={14} /> Open</a>
        </div>
        <div className="text-xs text-gray-500 mt-2">Public visitors submit their details here. {ev.require_approval_for_public ? "Registrations are held for approval." : "Tickets are issued instantly."}</div>
      </div>

      <div className="border border-gray-200 rounded-xl p-5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">Reports</div>
        <div className="flex flex-wrap gap-2 mt-3">
          <Btn variant="secondary" onClick={() => download("csv")} testId="vm-export-csv"><FileSpreadsheet size={14} /> Download CSV</Btn>
          <Btn variant="secondary" onClick={() => download("pdf")} testId="vm-export-pdf"><FileText size={14} /> Download PDF</Btn>
        </div>
        <div className="text-xs text-gray-500 mt-2">CSV includes all custom fields. PDF contains an event summary and full attendee list.</div>
      </div>
    </div>
  );
}

/* ============================================================
   Attendees tab — list, search, create
   ============================================================ */
function AttendeesTab({ ev, reload }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState(defaultAttendeeForm(ev));
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.append("q", q);
      if (statusFilter) params.append("status", statusFilter);
      const { data } = await api.get(`/vm/events/${ev.id}/attendees?${params.toString()}`);
      setRows(data.attendees || []);
    } catch { toast.error("Failed to load"); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [q, statusFilter]);

  async function create(e) {
    e.preventDefault();
    if (!form.full_name.trim()) { toast.error("Full name is required"); return; }
    setSaving(true);
    try {
      const body = {
        full_name: form.full_name.trim(),
        organisation: form.organisation.trim() || null,
        position: form.position.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        custom_answers: form.custom_answers,
        ticket_number: form.ticket_number ? Number(form.ticket_number) : null,
      };
      await api.post(`/vm/events/${ev.id}/attendees`, body);
      toast.success("Attendee registered");
      setModal(false);
      setForm(defaultAttendeeForm(ev));
      load(); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to add"); }
    finally { setSaving(false); }
  }

  async function del(a) {
    if (!window.confirm(`Remove ${a.full_name}? Their ticket will be released.`)) return;
    try {
      await api.delete(`/vm/attendees/${a.id}`);
      toast.success("Removed");
      load(); reload();
    } catch { toast.error("Failed"); }
  }

  async function resend(a) {
    try {
      const { data } = await api.post(`/vm/attendees/${a.id}/resend-pass`);
      if (data.sent) toast.success("Pass emailed");
      else toast.info("Email not configured yet — pass link is still available.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, org…"
            className="w-full border border-gray-200 rounded-md pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500"
            data-testid="vm-att-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-md px-3 py-2 text-sm" data-testid="vm-att-status">
          <option value="">All statuses</option>
          <option value="registered">Registered</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
          <option value="checked_in">Checked in</option>
        </select>
        <Btn variant="secondary" onClick={() => setBulkOpen(true)} testId="vm-att-bulk"><Upload size={14} /> Bulk import</Btn>
        <Btn onClick={() => setModal(true)} testId="vm-att-new"><Plus size={14} /> Add attendee</Btn>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={<Users size={18} />} title="No attendees yet" body="Add attendees manually or share the public registration link." />
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <table className="min-w-full text-sm" data-testid="vm-att-table">
            <thead className="bg-gray-50 text-left">
              <tr className="text-[10px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-3">Attendee</th>
                <th className="px-4 py-3">Ticket</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className="border-t border-gray-100" data-testid={`vm-att-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{a.full_name}</div>
                    <div className="text-xs text-gray-500">{a.email || a.phone || "—"} · {a.organisation || "—"}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-gray-800">{a.ticket_number ?? "—"}</td>
                  <td className="px-4 py-3"><StatusPill status={a.status} /></td>
                  <td className="px-4 py-3 text-xs text-gray-500 capitalize">{(a.source || "").replace("_", " ")}</td>
                  <td className="px-4 py-3 text-right">
                    {a.ticket_token && (
                      <>
                        <a href={`/vm/pass/${a.ticket_token}`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-gray-700 border border-gray-200 rounded-md px-2.5 py-1.5 hover:bg-gray-50 mr-2"
                          data-testid={`vm-att-pass-${a.id}`}>
                          <ExternalLink size={12} /> Pass
                        </a>
                        {a.email && (
                          <button onClick={() => resend(a)} className="inline-flex items-center gap-1 text-xs text-gray-700 border border-gray-200 rounded-md px-2.5 py-1.5 hover:bg-gray-50 mr-2" data-testid={`vm-att-resend-${a.id}`}>
                            <Mail size={12} /> Resend
                          </button>
                        )}
                      </>
                    )}
                    <button onClick={() => del(a)} className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1" data-testid={`vm-att-del-${a.id}`}>
                      <Trash2 size={12} /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title="Add attendee">
        <form onSubmit={create} className="space-y-3">
          <Input label="Full name *" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required testId="vm-att-name" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Organisation" value={form.organisation} onChange={(v) => setForm({ ...form, organisation: v })} testId="vm-att-org" />
            <Input label="Position" value={form.position} onChange={(v) => setForm({ ...form, position: v })} testId="vm-att-pos" />
            <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="vm-att-email" />
            <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="vm-att-phone" />
          </div>
          <Input label="Ticket number (optional — leave blank for next available)" type="number" value={form.ticket_number} onChange={(v) => setForm({ ...form, ticket_number: v })} testId="vm-att-tnum" />

          {(ev.custom_fields || []).length > 0 && (
            <div className="pt-3 border-t border-gray-100 space-y-3">
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Custom fields</div>
              {ev.custom_fields.map((f) => (
                <CustomFieldInput key={f.id} field={f}
                  value={form.custom_answers[f.id]}
                  onChange={(v) => setForm({ ...form, custom_answers: { ...form.custom_answers, [f.id]: v } })} />
              ))}
            </div>
          )}

          <div className="pt-3 flex justify-end gap-2">
            <Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn type="submit" disabled={saving} testId="vm-att-save">{saving ? "Adding…" : "Add attendee"}</Btn>
          </div>
        </form>
      </Modal>

      <BulkImportModal open={bulkOpen} onClose={() => setBulkOpen(false)} ev={ev} onDone={() => { load(); reload(); }} />
    </div>
  );
}

function BulkImportModal({ open, onClose, ev, onDone }) {
  const [csvText, setCsvText] = useState("name,organisation,position,email,phone,ticket_number\n");
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  function onFile(f) {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(f);
  }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await api.post(`/vm/events/${ev.id}/attendees/bulk`, { csv: csvText, send_email: sendEmail });
      setResult(data);
      if (data.created.length) toast.success(`Imported ${data.created.length} attendees`);
      if (data.errors.length) toast.warning(`${data.errors.length} row(s) skipped`);
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk import failed"); }
    finally { setSubmitting(false); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Bulk import attendees">
      {!result ? (
        <form onSubmit={submit} className="space-y-3" data-testid="vm-bulk-form">
          <div className="text-xs text-gray-600">
            Paste CSV rows below or upload a file. Columns accepted (any order):{" "}
            <code className="text-gray-800">name, organisation, position, email, phone, ticket_number</code>.
            Anything else becomes a custom answer. Missing <code>ticket_number</code> auto-assigns the next available.
          </div>
          <textarea rows={9} value={csvText} onChange={(e) => setCsvText(e.target.value)}
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-purple-500"
            data-testid="vm-bulk-csv" />
          <div className="flex items-center justify-between">
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
            <Btn variant="secondary" onClick={() => fileRef.current?.click()} testId="vm-bulk-upload"><Upload size={14} /> Upload CSV</Btn>
            <label className="text-xs text-gray-700 inline-flex items-center gap-2">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
              Email pass to each attendee
            </label>
          </div>
          <div className="pt-2 flex justify-end gap-2">
            <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
            <Btn type="submit" disabled={submitting} testId="vm-bulk-submit">{submitting ? "Importing…" : "Import"}</Btn>
          </div>
        </form>
      ) : (
        <div className="space-y-3" data-testid="vm-bulk-result">
          <div className="text-sm text-gray-800">Imported <b>{result.created.length}</b> of <b>{result.total}</b> row(s).</div>
          {result.errors?.length > 0 && (
            <div className="border border-amber-200 bg-amber-50 rounded-md p-3 text-xs text-amber-900 space-y-1 max-h-40 overflow-y-auto">
              {result.errors.map((e, i) => (<div key={i}>Row {e.row}{e.name ? ` (${e.name})` : ""}: {e.error}</div>))}
            </div>
          )}
          <div className="flex justify-end">
            <Btn onClick={onClose}>Done</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

function defaultAttendeeForm() { return { full_name: "", organisation: "", position: "", email: "", phone: "", ticket_number: "", custom_answers: {} }; }

function CustomFieldInput({ field, value, onChange }) {
  if (field.type === "long_text") return <Input label={`${field.label}${field.required ? " *" : ""}`} value={value || ""} onChange={onChange} rows={3} required={field.required} />;
  if (field.type === "single_choice") return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">{field.label}{field.required ? " *" : ""}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full border border-gray-200 rounded-md px-3 py-2 text-sm">
        <option value="">Select…</option>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
  if (field.type === "multi_choice") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>
        <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">{field.label}{field.required ? " *" : ""}</label>
        <div className="grid grid-cols-2 gap-2 mt-1.5">
          {field.options.map((o) => {
            const sel = arr.includes(o);
            return (
              <button key={o} type="button" onClick={() => onChange(sel ? arr.filter((x) => x !== o) : [...arr, o])}
                className={`text-sm text-left border rounded-md px-3 py-2 ${sel ? "border-purple-600 bg-purple-50 text-purple-900" : "border-gray-200 hover:border-gray-300"}`}>
                {o}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  return <Input label={`${field.label}${field.required ? " *" : ""}`} value={value || ""} onChange={onChange} required={field.required} />;
}

/* ============================================================
   Tickets tab
   ============================================================ */
function TicketsTab({ ev, reload }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [modal, setModal] = useState(false);
  const [range, setRange] = useState({ start: ev.ticket_end ? String(ev.ticket_end + 1) : "1000", end: "" });
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.append("q", q);
      if (status) params.append("status", status);
      const { data } = await api.get(`/vm/events/${ev.id}/tickets?${params.toString()}`);
      setRows(data.tickets || []);
    } catch { toast.error("Failed to load"); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [q, status]);

  async function generate(e) {
    e.preventDefault();
    if (!range.start || !range.end) { toast.error("Enter start and end"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/vm/events/${ev.id}/tickets/generate`, { start: Number(range.start), end: Number(range.end) });
      toast.success(`Generated ${data.created} tickets`);
      setModal(false);
      load(); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  }

  async function downloadQrZip() {
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      params.append("base", window.location.origin);
      const url = `${API}/vm/events/${ev.id}/tickets/qr.zip?${params.toString()}`;
      const token = localStorage.getItem("expoflo_token");
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${ev.slug}-qr-codes${status ? "-" + status : ""}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      toast.success("QR codes bundled");
    } catch (err) {
      toast.error(String(err.message || err) || "Download failed");
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ticket number…"
            className="w-full border border-gray-200 rounded-md pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500"
            data-testid="vm-ticket-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="border border-gray-200 rounded-md px-3 py-2 text-sm" data-testid="vm-ticket-status">
          <option value="">All statuses</option>
          <option value="available">Available</option>
          <option value="assigned">Assigned</option>
          <option value="registered">Registered</option>
          <option value="checked_in">Checked in</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <Btn variant="secondary" onClick={downloadQrZip} testId="vm-ticket-qr-zip"><QrCode size={14} /> Download QR ZIP</Btn>
        <Btn onClick={() => setModal(true)} testId="vm-ticket-gen"><Plus size={14} /> Generate tickets</Btn>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={<Ticket size={18} />} title="No tickets in this range yet" body="Generate a ticket sequence to build your inventory." />
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr className="text-[10px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-3">Number</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Attendee</th>
                <th className="px-4 py-3">QR</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-t border-gray-100" data-testid={`vm-ticket-row-${t.ticket_number}`}>
                  <td className="px-4 py-3 font-mono text-gray-900">#{t.ticket_number}</td>
                  <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                  <td className="px-4 py-3 text-xs text-gray-500 font-mono">{t.attendee_id ? t.attendee_id.slice(0, 8) : "—"}</td>
                  <td className="px-4 py-3">
                    <a href={`${API}/public/vm/qr/${t.token}.png?base=${encodeURIComponent(window.location.origin)}`}
                      target="_blank" rel="noreferrer"
                      className="text-xs text-gray-700 border border-gray-200 rounded-md px-2 py-1 inline-flex items-center gap-1 hover:bg-gray-50"
                      data-testid={`vm-ticket-qr-${t.ticket_number}`}>
                      <QrCode size={12} /> View QR
                    </a>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <a href={`/vm/ticket/${t.token}`} target="_blank" rel="noreferrer"
                      className="text-xs text-gray-700 border border-gray-200 rounded-md px-2.5 py-1.5 hover:bg-gray-50 inline-flex items-center gap-1">
                      <ExternalLink size={12} /> Registration link
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title="Generate ticket sequence">
        <form onSubmit={generate} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="From (start)" type="number" value={range.start} onChange={(v) => setRange({ ...range, start: v })} required />
            <Input label="To (end)" type="number" value={range.end} onChange={(v) => setRange({ ...range, end: v })} required />
          </div>
          <div className="text-xs text-gray-500">Each ticket gets a unique scannable QR code. Existing numbers in this range are skipped.</div>
          <div className="pt-3 flex justify-end gap-2">
            <Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn type="submit" disabled={saving}>{saving ? "Generating…" : "Generate"}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}

/* ============================================================
   Approvals tab
   ============================================================ */
function ApprovalsTab({ ev, reload }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/vm/events/${ev.id}/attendees?status=pending`);
      setRows(data.attendees || []);
    } catch { toast.error("Failed to load"); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function approve(a) {
    try {
      await api.post(`/vm/attendees/${a.id}/approve`);
      toast.success("Approved and ticket issued");
      load(); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  }
  async function reject(a) {
    const reason = window.prompt("Rejection reason (optional):", "");
    try {
      await api.post(`/vm/attendees/${a.id}/reject`, { reason });
      toast.success("Registration rejected");
      load(); reload();
    } catch { toast.error("Failed"); }
  }

  if (loading) return <div className="text-sm text-gray-500">Loading…</div>;
  if (rows.length === 0) return <EmptyState icon={<ClipboardCheck size={18} />} title="No pending approvals" body="Public registrations awaiting review will show up here." />;

  return (
    <div className="space-y-3" data-testid="vm-approvals-list">
      {rows.map((a) => (
        <div key={a.id} className="border border-gray-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-medium text-gray-900">{a.full_name}</div>
            <div className="text-xs text-gray-500">{a.email || a.phone || "—"} · {a.organisation || "—"} · {a.position || "—"}</div>
            <div className="text-[11px] text-gray-400 mt-1">Submitted {a.created_at?.slice(0, 16).replace("T", " ")}</div>
          </div>
          <div className="flex gap-2">
            <Btn variant="secondary" onClick={() => reject(a)} testId={`vm-app-reject-${a.id}`}><X size={14} /> Reject</Btn>
            <Btn onClick={() => approve(a)} testId={`vm-app-approve-${a.id}`}><ShieldCheck size={14} /> Approve & issue ticket</Btn>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   Check-in tab — QR camera scan + manual entry
   ============================================================ */
function CheckinTab({ ev, reload }) {
  const [manual, setManual] = useState("");
  const [last, setLast] = useState(null);
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const detectorRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const lastScanRef = useRef("");

  useEffect(() => () => stopCamera(), []);

  async function processInput({ ticket_number, token }) {
    try {
      const { data } = await api.post(`/vm/events/${ev.id}/checkin`, { ticket_number, token });
      setLast({ ok: true, ...data });
      if (data.duplicate) toast.warning("Already checked in");
      else toast.success(`Checked in: ${data.attendee?.full_name || `#${data.ticket?.ticket_number}`}`);
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Check-in failed";
      setLast({ ok: false, error: detail });
      toast.error(detail);
    }
  }

  function submitManual(e) {
    e.preventDefault();
    if (!manual.trim()) return;
    processInput({ ticket_number: Number(manual.trim()) });
    setManual("");
  }

  async function startCamera() {
    if (!("BarcodeDetector" in window)) {
      toast.error("This browser does not support built-in QR scanning. Use manual entry.");
      return;
    }
    try {
      detectorRef.current = new window.BarcodeDetector({ formats: ["qr_code"] });
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setScanning(true);
      loop();
    } catch (err) {
      toast.error("Could not access camera — please allow permission or use manual entry.");
    }
  }
  function stopCamera() {
    setScanning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }
  async function loop() {
    if (!detectorRef.current || !videoRef.current) return;
    try {
      const codes = await detectorRef.current.detect(videoRef.current);
      if (codes && codes.length > 0) {
        const raw = codes[0].rawValue;
        if (raw && raw !== lastScanRef.current) {
          lastScanRef.current = raw;
          // Extract token from URL like /vm/pass/<token> or /vm/ticket/<token>
          const match = raw.match(/(?:pass|ticket)\/([^/?#]+)/);
          const token = match ? match[1] : raw;
          await processInput({ token });
          // Small cooldown to avoid double scan
          setTimeout(() => { lastScanRef.current = ""; }, 1200);
        }
      }
    } catch { /* ignore intermittent detection errors */ }
    rafRef.current = requestAnimationFrame(loop);
  }

  return (
    <div className="space-y-5">
      <StaffCheckinLink ev={ev} />
      <div className="grid md:grid-cols-2 gap-5">
      <div className="border border-gray-200 rounded-xl p-5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">QR scanner</div>
        <div className="mt-3 aspect-video bg-gray-900 rounded-lg overflow-hidden flex items-center justify-center relative">
          <video ref={videoRef} playsInline muted className={`w-full h-full object-cover ${scanning ? "" : "hidden"}`} data-testid="vm-scan-video" />
          {!scanning && <div className="text-white/60 text-sm">Camera preview</div>}
        </div>
        <div className="mt-3 flex gap-2">
          {!scanning ? (
            <Btn onClick={startCamera} testId="vm-scan-start"><Camera size={14} /> Start scanning</Btn>
          ) : (
            <Btn variant="secondary" onClick={stopCamera} testId="vm-scan-stop"><X size={14} /> Stop</Btn>
          )}
        </div>
        <div className="text-xs text-gray-500 mt-2">Point the tablet&apos;s camera at the visitor&apos;s QR code — check-ins process automatically.</div>
      </div>

      <div className="space-y-4">
        <form onSubmit={submitManual} className="border border-gray-200 rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">Manual entry</div>
          <div className="flex gap-2 mt-3">
            <input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Ticket number, e.g. 1023"
              className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm"
              data-testid="vm-checkin-manual-input" inputMode="numeric" />
            <Btn type="submit" testId="vm-checkin-manual-submit"><CheckSquare size={14} /> Check in</Btn>
          </div>
        </form>

        {last && (
          <div className={`border rounded-xl p-5 ${last.ok ? (last.duplicate ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50") : "border-red-200 bg-red-50"}`}
            data-testid="vm-checkin-result">
            {last.ok ? (
              <>
                <div className={`text-xs uppercase tracking-widest font-bold ${last.duplicate ? "text-amber-800" : "text-emerald-800"}`}>
                  {last.duplicate ? "Already checked in" : "Checked in"}
                </div>
                <div className="font-display text-xl font-semibold text-gray-900 mt-2">{last.attendee?.full_name || "—"}</div>
                <div className="text-sm text-gray-600 mt-1">Ticket #{last.ticket?.ticket_number} · {last.attendee?.organisation || "—"} · {last.attendee?.email || last.attendee?.phone || "—"}</div>
                {last.ticket?.checked_in_at && <div className="text-xs text-gray-500 mt-2 inline-flex items-center gap-1"><Clock size={12} /> {last.ticket.checked_in_at.replace("T", " ").slice(0, 19)}</div>}
              </>
            ) : (
              <>
                <div className="text-xs uppercase tracking-widest font-bold text-red-800">Check-in rejected</div>
                <div className="text-sm text-gray-800 mt-2">{last.error}</div>
              </>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

/* ============================================================
   Staff check-in link card — shareable mobile URL + QR + regenerate
   ============================================================ */
function StaffCheckinLink({ ev }) {
  const [token, setToken] = useState(ev.checkin_token || null);
  const [busy, setBusy] = useState(false);
  const url = token ? `${window.location.origin}/vm/checkin/${token}` : null;
  const qrSrc = token ? `${API}/public/vm/qr/${token}.png?base=${encodeURIComponent(window.location.origin)}` : null;

  async function generate(regen) {
    if (regen && !window.confirm("Regenerate the check-in link? Anyone with the previous link will lose access immediately.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/vm/events/${ev.id}/checkin-link`);
      setToken(data.checkin_token);
      toast.success(regen ? "Link regenerated — old link revoked" : "Check-in link created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  function copy() {
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  }

  return (
    <div className="border border-gray-200 rounded-xl p-5" data-testid="vm-checkin-link-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">Staff check-in link</div>
          <div className="text-sm text-gray-600 mt-1">Share this URL with your team. Opens a mobile-first check-in app — no login required. Regenerate any time to instantly revoke it.</div>
        </div>
      </div>

      {!token ? (
        <div className="mt-4">
          <Btn onClick={() => generate(false)} disabled={busy} testId="vm-checkin-link-create">
            {busy ? "Generating…" : "Generate check-in link"}
          </Btn>
        </div>
      ) : (
        <div className="mt-4 flex flex-col md:flex-row gap-4 items-start">
          <img src={qrSrc} alt="Check-in link QR" className="w-32 h-32 border border-gray-200 rounded-lg p-1 bg-white shrink-0" data-testid="vm-checkin-link-qr" />
          <div className="flex-1 min-w-0 space-y-2">
            <input readOnly value={url} className="w-full font-mono text-sm border border-gray-200 rounded-md px-3 py-2 bg-gray-50" data-testid="vm-checkin-link-url" onFocus={(e) => e.target.select()} />
            <div className="flex flex-wrap gap-2">
              <Btn variant="secondary" onClick={copy} testId="vm-checkin-link-copy"><Copy size={14} /> Copy link</Btn>
              <a href={url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-gray-700 border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50"
                data-testid="vm-checkin-link-open">
                <ExternalLink size={14} /> Open
              </a>
              <Btn variant="secondary" onClick={() => generate(true)} disabled={busy} testId="vm-checkin-link-regen">
                <RefreshCw size={14} /> {busy ? "Regenerating…" : "Regenerate"}
              </Btn>
            </div>
            <div className="text-xs text-gray-500">Staff scan the QR above with any phone camera to launch the check-in app.</div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Settings tab — event edit + logo + custom fields
   ============================================================ */
function SettingsTab({ ev, reload }) {
  const [form, setForm] = useState({
    name: ev.name || "", description: ev.description || "", event_date: ev.event_date || "",
    start_time: ev.start_time || "", end_time: ev.end_time || "", location: ev.location || "",
    capacity: ev.capacity || 0,
    rules_and_settings: ev.rules_and_settings || "",
    public_registration_open: ev.public_registration_open !== false,
    require_approval_for_public: ev.require_approval_for_public !== false,
    custom_fields: ev.custom_fields || [],
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/vm/events/${ev.id}`, form);
      toast.success("Saved");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }

  async function uploadLogo(file) {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "logo");
      await api.post(`/vm/events/${ev.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      reload();
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  }

  function addField() {
    setForm({
      ...form,
      custom_fields: [...form.custom_fields, { id: `f_${Date.now()}`, label: "New field", type: "short_text", required: false, options: [], order: form.custom_fields.length }],
    });
  }
  function updateField(i, patch) {
    const arr = [...form.custom_fields];
    arr[i] = { ...arr[i], ...patch };
    setForm({ ...form, custom_fields: arr });
  }
  function removeField(i) {
    const arr = [...form.custom_fields]; arr.splice(i, 1); setForm({ ...form, custom_fields: arr });
  }

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-5">
        <div className="border border-gray-200 rounded-xl p-5 space-y-3">
          <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Event details</div>
          <Input label="Event name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Input label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} rows={3} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Date" type="date" value={form.event_date} onChange={(v) => setForm({ ...form, event_date: v })} />
            <Input label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} />
            <Input label="Start time" type="time" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} />
            <Input label="End time" type="time" value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} />
          </div>
          <Input label="Capacity" type="number" value={form.capacity} onChange={(v) => setForm({ ...form, capacity: v })} />
          <Input label="Rules & settings" value={form.rules_and_settings} onChange={(v) => setForm({ ...form, rules_and_settings: v })} rows={3} />
        </div>

        <div className="space-y-5">
          <div className="border border-gray-200 rounded-xl p-5">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Event logo</div>
            {ev.logo_path ? (
              <img src={fileUrl(ev.logo_path)} alt="Event logo" className="h-16 object-contain mt-3" data-testid="vm-event-logo" />
            ) : (
              <div className="mt-3 text-sm text-gray-500">No logo uploaded yet.</div>
            )}
            <div className="mt-3">
              <input ref={fileRef} type="file" accept="image/*" onChange={(e) => uploadLogo(e.target.files?.[0])} className="hidden" />
              <Btn variant="secondary" onClick={() => fileRef.current?.click()} disabled={uploading} testId="vm-logo-upload">
                <Upload size={14} /> {uploading ? "Uploading…" : "Upload logo"}
              </Btn>
              <div className="text-xs text-gray-400 mt-2">This logo appears on attendee passes and public registration.</div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-xl p-5 space-y-3">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Public registration</div>
            <label className="flex items-center gap-2 text-sm text-gray-800">
              <input type="checkbox" checked={form.public_registration_open} onChange={(e) => setForm({ ...form, public_registration_open: e.target.checked })} />
              Allow public registration link
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-800">
              <input type="checkbox" checked={form.require_approval_for_public} onChange={(e) => setForm({ ...form, require_approval_for_public: e.target.checked })} />
              Require admin approval before issuing tickets
            </label>
          </div>
        </div>
      </div>

      <div className="border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Custom attendee fields</div>
          <Btn variant="secondary" onClick={addField} testId="vm-cf-add"><Plus size={14} /> Add field</Btn>
        </div>
        {form.custom_fields.length === 0 ? (
          <div className="text-sm text-gray-500 mt-3">No custom fields. The standard fields (name, organisation, position, email, phone) are always collected.</div>
        ) : (
          <div className="mt-4 space-y-3">
            {form.custom_fields.map((f, i) => (
              <div key={f.id} className="border border-gray-200 rounded-lg p-3 grid md:grid-cols-12 gap-2 items-start">
                <div className="md:col-span-4">
                  <Input label="Label" value={f.label} onChange={(v) => updateField(i, { label: v })} />
                </div>
                <div className="md:col-span-3">
                  <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">Type</label>
                  <select value={f.type} onChange={(e) => updateField(i, { type: e.target.value })} className="mt-1.5 w-full border border-gray-200 rounded-md px-3 py-2 text-sm">
                    <option value="short_text">Short text</option>
                    <option value="long_text">Long text</option>
                    <option value="single_choice">Single choice</option>
                    <option value="multi_choice">Multiple choice</option>
                  </select>
                </div>
                <div className="md:col-span-3">
                  {(f.type === "single_choice" || f.type === "multi_choice") ? (
                    <Input label="Options (comma-separated)"
                      value={(f.options || []).join(", ")}
                      onChange={(v) => updateField(i, { options: v.split(",").map((o) => o.trim()).filter(Boolean) })} />
                  ) : <div className="text-xs text-gray-400 mt-6">—</div>}
                </div>
                <div className="md:col-span-1 pt-6">
                  <label className="text-xs text-gray-600 inline-flex items-center gap-1">
                    <input type="checkbox" checked={!!f.required} onChange={(e) => updateField(i, { required: e.target.checked })} />
                    Req
                  </label>
                </div>
                <div className="md:col-span-1 pt-6 text-right">
                  <button onClick={() => removeField(i)} className="text-red-600 hover:text-red-700" aria-label="Remove"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Btn onClick={save} disabled={saving} testId="vm-settings-save">{saving ? "Saving…" : "Save changes"}</Btn>
      </div>
    </div>
  );
}

/* ============================================================
   Status pill
   ============================================================ */
/* ============================================================
   Analytics tab — timelines, peaks, source & status breakdowns
   ============================================================ */
function AnalyticsTab({ ev }) {
  const [ctx, setCtx] = useState(null);
  const [bucket, setBucket] = useState("hour");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/vm/events/${ev.id}/analytics?bucket=${bucket}`);
      setCtx(data);
    } catch { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [bucket]);

  if (loading || !ctx) return <div className="text-sm text-gray-500">Loading analytics…</div>;

  const merged = mergeTimelines(ctx.registration_timeline, ctx.checkin_timeline);
  const SOURCE_COLORS = ["#7C3AED", "#EC4899", "#111827", "#6b7280", "#22c55e"];

  return (
    <div className="space-y-6" data-testid="vm-analytics">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">Time bucket</div>
        <select value={bucket} onChange={(e) => setBucket(e.target.value)}
          className="border border-gray-200 rounded-md px-2 py-1 text-sm" data-testid="vm-analytics-bucket">
          <option value="hour">Hourly</option>
          <option value="day">Daily</option>
        </select>
        {ctx.peak_checkin && (
          <div className="ml-auto text-xs text-gray-600 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1"
            data-testid="vm-analytics-peak">
            <TrendingUp size={12} className="text-emerald-700" />
            Peak check-in: <b className="font-mono">{ctx.peak_checkin.bucket}</b> ({ctx.peak_checkin.count})
          </div>
        )}
      </div>

      <div className="border border-gray-200 rounded-xl p-5">
        <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Registrations vs check-ins</div>
        <div className="h-72 mt-3">
          {merged.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-gray-400">No activity yet</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={merged}>
                <CartesianGrid stroke="#f3f4f6" strokeDasharray="3 3" />
                <XAxis dataKey="t" tick={{ fontSize: 11, fill: "#6b7280" }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="registrations" stroke="#7C3AED" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="checkins" stroke="#22c55e" strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="border border-gray-200 rounded-xl p-5">
          <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Registration source</div>
          <div className="h-64 mt-3">
            {ctx.source_breakdown.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-gray-400">No registrations yet</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={ctx.source_breakdown} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={80} label={(d) => `${humaniseSource(d.source)}: ${d.count}`}>
                    {ctx.source_breakdown.map((_, i) => <Cell key={i} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, humaniseSource(n)]} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="border border-gray-200 rounded-xl p-5">
          <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Status breakdown</div>
          <div className="h-64 mt-3">
            {ctx.status_breakdown.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-gray-400">No data</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ctx.status_breakdown} layout="vertical">
                  <CartesianGrid stroke="#f3f4f6" strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} />
                  <YAxis type="category" dataKey="status" tick={{ fontSize: 11, fill: "#6b7280" }} width={90}
                         tickFormatter={humaniseStatus} />
                  <Tooltip formatter={(v) => v} labelFormatter={humaniseStatus} contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="count" fill="#7C3AED" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function mergeTimelines(a, b) {
  const keys = new Set([...(a || []).map((r) => r.t), ...(b || []).map((r) => r.t)]);
  const aMap = new Map((a || []).map((r) => [r.t, r.count]));
  const bMap = new Map((b || []).map((r) => [r.t, r.count]));
  return Array.from(keys).sort().map((t) => ({ t, registrations: aMap.get(t) || 0, checkins: bMap.get(t) || 0 }));
}
function humaniseSource(s) {
  return ({ manual: "Manual", existing_ticket: "Existing ticket", public: "Public link" })[s] || s;
}
function humaniseStatus(s) {
  return ({ registered: "Registered", pending: "Pending", checked_in: "Checked in", rejected: "Rejected" })[s] || s;
}


function StatusPill({ status }) {
  const map = {
    available:   ["Available",   "bg-gray-100 text-gray-700 border-gray-200"],
    assigned:    ["Assigned",    "bg-blue-50 text-blue-800 border-blue-100"],
    registered:  ["Registered",  "bg-purple-50 text-purple-800 border-purple-100"],
    checked_in:  ["Checked in",  "bg-emerald-50 text-emerald-800 border-emerald-100"],
    pending:     ["Pending",     "bg-amber-50 text-amber-800 border-amber-100"],
    rejected:    ["Rejected",    "bg-red-50 text-red-700 border-red-100"],
    cancelled:   ["Cancelled",   "bg-red-50 text-red-700 border-red-100"],
  };
  const [label, cls] = map[status] || [status || "—", "bg-gray-100 text-gray-700 border-gray-200"];
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium border ${cls}`}>{label}</span>;
}
